import React from 'react';
import styled from 'styled-components';
import { formatCurrency } from '../../utils/formatters';

const ResultContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-top: 2rem;
`;

const ResultTitle = styled.h2`
  color: #333;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 0.5rem;
`;

const ResultSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  color: #555;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #666;
`;

const InfoValue = styled.span`
  font-weight: 500;
  color: #333;
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #dee2e6;
`;

const TableCell = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;
`;

const TotalSection = styled.div`
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 1rem;
  margin-top: 1rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  font-size: ${props => props.isTotal ? '1.2rem' : '1rem'};
  font-weight: ${props => props.isTotal ? '700' : '400'};
  color: ${props => props.isTotal ? '#4CAF50' : '#333'};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${props => props.variant === 'primary' ? '#4CAF50' : '#6c757d'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: ${props => props.variant === 'primary' ? '#45a049' : '#5a6268'};
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: #e3f2fd;
  color: #1976d2;
  border-radius: 4px;
  font-size: 0.875rem;
  margin-left: 0.5rem;
`;

const CalculationResult = ({ calculation, onSave, loading }) => {
  const { client, items, subtotal, discount, discountAmount, total, units, fingerprint, displayName, pricePerUnit } = calculation;

  const handlePrint = () => {
    window.print();
  };

  return (
    <ResultContainer>
      <ResultTitle>
        Resultado del Cálculo
      </ResultTitle>

      <ResultSection>
        <InfoRow>
          <InfoLabel>Descripción:</InfoLabel>
          <InfoValue>{displayName}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Huella:</InfoLabel>
          <InfoValue><Badge>{fingerprint}</Badge></InfoValue>
        </InfoRow>
      </ResultSection>

      <ResultSection>
        <SectionTitle>Información del Cliente</SectionTitle>
        <InfoRow>
          <InfoLabel>Cliente:</InfoLabel>
          <InfoValue>{client.name}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Margen aplicado:</InfoLabel>
          <InfoValue>{client.marginPercentage}%</InfoValue>
        </InfoRow>
      </ResultSection>

      <ResultSection>
        <SectionTitle>Detalle de Materiales</SectionTitle>
        <ItemsTable>
          <thead>
            <tr>
              <TableHeader>Material</TableHeader>
              <TableHeader>Producto</TableHeader>
              <TableHeader>Dimensiones</TableHeader>
              <TableHeader>Cantidad</TableHeader>
              <TableHeader>Precio Unit.</TableHeader>
              <TableHeader>Total</TableHeader>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={`${item.type}-${item.productId || index}`}>
                <TableCell>{item.type}</TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>
                  {item.width && item.height && `${item.width}x${item.height} cm`}
                  {item.linearMeters && ` (${item.linearMeters.toFixed(2)} m)`}
                  {item.squareMeters && ` (${item.squareMeters.toFixed(2)} m²)`}
                </TableCell>
                <TableCell>{item.quantity || 1}</TableCell>
                <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                <TableCell>{formatCurrency(item.totalPrice)}</TableCell>
              </tr>
            ))}
          </tbody>
        </ItemsTable>
      </ResultSection>

      <TotalSection>
        <TotalRow>
          <span>Subtotal:</span>
          <span>{formatCurrency(subtotal)}</span>
        </TotalRow>
        
        {units > 1 && (
          <TotalRow>
            <span>Unidades:</span>
            <span>x{units}</span>
          </TotalRow>
        )}
        
        {discount > 0 && (
          <>
            <TotalRow>
              <span>Descuento ({discount}%):</span>
              <span>-{formatCurrency(discountAmount)}</span>
            </TotalRow>
          </>
        )}
        
        <TotalRow isTotal>
          <span>Total:</span>
          <span>{formatCurrency(total)}</span>
        </TotalRow>
        
        {units > 1 && (
          <TotalRow>
            <span>Precio por unidad:</span>
            <span>{formatCurrency(pricePerUnit)}</span>
          </TotalRow>
        )}
      </TotalSection>

      <ButtonGroup>
        <Button variant="secondary" onClick={handlePrint}>
          Imprimir
        </Button>
        <Button variant="primary" onClick={onSave} disabled={loading}>
          {loading ? 'Guardando...' : 'Guardar Pedido'}
        </Button>
      </ButtonGroup>
    </ResultContainer>
  );
};

export default CalculationResult;